import { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Stack, Checkbox, FormControlLabel } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";

const OTPStep = ({ tempToken, onBack, onBackToLogin, redirectUrl }) => {
	const theme = useTheme();
	const [digits, setDigits] = useState(["", "", "", "", "", ""]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [verified, setVerified] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [rememberDevice, setRememberDevice] = useState(false);
	const inputRefs = useRef([]);

	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	const handleChange = (index, value) => {
		const cleaned = value.replace(/\D/g, "").slice(-1);
		const next = [...digits];
		next[index] = cleaned;
		setDigits(next);
		setError("");
		if (cleaned && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index, e) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			const next = [...digits];
			next[index - 1] = "";
			setDigits(next);
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		const next = [...digits];
		pasted.split("").forEach((ch, i) => {
			if (next[i] !== undefined) next[i] = ch;
		});
		setDigits(next);
		inputRefs.current[Math.min(pasted.length, 5)]?.focus();
	};

	const isFull = digits.every((d) => d !== "");
	const code = digits.join("");

	const handleVerify = async () => {
		if (!isFull || loading) return;
		setLoading(true);
		setError("");

		try {
			const response = await fetch(`${window.location.protocol}//${window.location.host}/wp-json/tmt-portal/v1/verify-otp/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ temp_token: tempToken, otp: code, remember_device: rememberDevice }),
			});

			const data = await response.json();

			if (data.user) {
				localStorage.removeItem("tmt_mfa_step");
				localStorage.removeItem("tmt_mfa_data");
				setVerified(true);
				setError("");
				setTimeout(() => {
					setIsRedirecting(true);
					setTimeout(() => {
						window.location.href = redirectUrl;
					}, 800);
				}, 1000);
			} else {
				setError(data.message || "Invalid code. Please try again.");
				setDigits(["", "", "", "", "", ""]);
				inputRefs.current[0]?.focus();
			}
		} catch (err) {
			console.error("OTP verification error:", err);
			setError("Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (verified && isRedirecting) {
			localStorage.removeItem("tmt_mfa_step");
			localStorage.removeItem("tmt_mfa_data");
		}
	}, [verified, isRedirecting]);

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: 750,
				mx: "auto",
				bgcolor: theme.palette.background.paper,
				borderRadius: "8px",
				border: `1px solid #E6EBF4`,
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid #E6EBF4`, display: "flex", alignItems: "center", gap: 1.5 }}>
				<Box
					component="button"
					type="button"
					onClick={onBack}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: 30,
						height: 30,
						borderRadius: "7px",
						border: `1px solid #E6EBF4`,
						bgcolor: "transparent !important",
						color: theme.palette.primary.main,
						cursor: "pointer",
						flexShrink: 0,
						"&:hover": { bgcolor: theme.palette.secondary.lightbgcolor },
						transition: "background-color 0.15s",
					}}
				>
					<ArrowBackIcon sx={{ fontSize: 16 }} />
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: 36,
						height: 36,
						borderRadius: "9px",
						bgcolor: theme.palette.secondary.lightbgcolor,
						color: theme.palette.primary.main,
					}}
				>
					<ShieldOutlinedIcon sx={{ fontSize: 20 }} />
				</Box>
				<Box>
					<Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: theme.palette.text.primary, lineHeight: 1.2 }}>
						Two-Factor Authentication
					</Typography>
					<Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, mt: 0.25 }}>
						Enter the code from your authenticator app
					</Typography>
				</Box>
			</Box>

			{/* Body */}
			<Box sx={{ px: 3, py: 4, maxWidth: 400, mx: "auto", textAlign: "center" }}>
				<Typography sx={{ fontSize: "0.85rem", color: theme.palette.text.secondary, mb: 3, lineHeight: 1.6 }}>
					Open <strong>Google Authenticator</strong>, <strong>Authy</strong>, or your chosen authenticator and enter the 6-digit code shown
					for this account.
				</Typography>

				{/* Label */}
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 0.75, mb: 1.5 }}>
					<Typography sx={{ fontSize: "0.78rem", color: theme.palette.text.secondary }}>Verification code:</Typography>
				</Box>

				{/* Digit inputs */}
				<Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
					{digits.map((digit, i) => (
						<Box
							key={i}
							component="input"
							ref={(el) => (inputRefs.current[i] = el)}
							type="text"
							inputMode="numeric"
							maxLength={1}
							value={digit}
							onChange={(e) => handleChange(i, e.target.value)}
							onKeyDown={(e) => handleKeyDown(i, e)}
							onPaste={handlePaste}
							sx={{
								width: 48,
								height: 56,
								border: error
									? `1.5px solid ${theme.palette.error.main}`
									: digit
										? `1.5px solid ${theme.palette.primary.main}`
										: `1.5px solid #E6EBF4`,
								borderRadius: "8px",
								fontSize: "1.4rem",
								fontWeight: 700,
								textAlign: "center",
								color: theme.palette.text.primary,
								bgcolor: theme.palette.background.paper,
								outline: "none",
								caretColor: "transparent",
								transition: "border-color 0.15s, box-shadow 0.15s",
								"&:focus": {
									borderColor: theme.palette.primary.main,
									boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
								},
							}}
						/>
					))}
				</Stack>

				{/* Error */}
				{error && <Typography sx={{ fontSize: "0.78rem", color: theme.palette.error.main, mb: 2, textAlign: "left" }}>{error}</Typography>}

				{/* Remember Device */}
				<Box
					onClick={() => setRememberDevice((prev) => !prev)}
					sx={{
						display: "flex",
						alignItems: "flex-start",
						gap: 1.25,
						mb: 2.5,
						p: 1.5,
						borderRadius: "9px",
						border: rememberDevice ? `1.5px solid ${theme.palette.primary.main}` : `1.5px solid #E6EBF4`,
						bgcolor: rememberDevice ? theme.palette.secondary.lightbgcolor : theme.palette.background.default,
						cursor: "pointer",
						textAlign: "left",
						transition: "border-color 0.15s, background-color 0.15s",
					}}
				>
					{/* Device icon */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: "7px",
							bgcolor: rememberDevice ? theme.palette.secondary.lightbgcolor : theme.palette.grey.main,
							color: rememberDevice ? theme.palette.primary.main : theme.palette.text.secondary,
							flexShrink: 0,
							mt: 0.1,
							transition: "background-color 0.15s, color 0.15s",
						}}
					>
						<DevicesOutlinedIcon sx={{ fontSize: 17 }} />
					</Box>

					<Box sx={{ flex: 1 }}>
						<Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3, mb: 0.3 }}>
							Remember this device
						</Typography>
						<Typography sx={{ fontSize: "0.74rem", color: theme.palette.text.secondary, lineHeight: 1.5 }}>
							You won’t be required to enter an OTP for the next 7 days. It is not recommended to enable this on shared or public
							devices.
						</Typography>
					</Box>

					{/* Custom checkbox */}
					<Box
						sx={{
							width: 18,
							height: 18,
							borderRadius: "5px",
							border: rememberDevice ? `2px solid ${theme.palette.primary.main}` : `2px solid #E6EBF4`,
							bgcolor: rememberDevice ? theme.palette.primary.main : "transparent",
							flexShrink: 0,
							mt: 0.2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "border-color 0.15s, background-color 0.15s",
						}}
					>
						{rememberDevice && (
							<Box
								component="svg"
								viewBox="0 0 12 10"
								sx={{
									width: 10,
									height: 10,
									fill: "none",
									stroke: theme.palette.primary.contrastText,
									strokeWidth: 2,
									strokeLinecap: "round",
									strokeLinejoin: "round",
								}}
							>
								<polyline points="1,5 4.5,8.5 11,1" />
							</Box>
						)}
					</Box>
				</Box>

				{/* Verify button */}
				<Button
					fullWidth
					variant="contained"
					disabled={!isFull || loading || verified}
					onClick={handleVerify}
					sx={{
						py: 1.25,
						fontWeight: 700,
						fontSize: "0.88rem",
						bgcolor: verified ? theme.palette.success.main : theme.palette.primary.main,
						borderRadius: "9px",
						textTransform: "none",
						boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
						"&:hover": { bgcolor: verified ? theme.palette.success.dark : theme.palette.primary.dark },
						"&:disabled": { bgcolor: theme.palette.grey.main, color: theme.palette.text.secondary, boxShadow: "none" },
						transition: "background-color 0.2s",
					}}
				>
					{isRedirecting ? "Redirecting..." : verified ? "✓ Verified" : loading ? "Verifying..." : "Verify"}
				</Button>

				{/* Hint */}
				<Typography sx={{ fontSize: "0.72rem", color: theme.palette.text.secondary, mt: 1.5, lineHeight: 1.5 }}>
					Codes refresh every 30 seconds — use the current code shown in your authenticator.
				</Typography>

				{/* Escape hatch back to the login form */}
				<Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #E6EBF4" }}>
					<Button
						onClick={onBackToLogin}
						disabled={loading || verified}
						sx={{
							fontSize: "0.78rem",
							fontWeight: 600,
							color: theme.palette.text.secondary,
							textTransform: "none",
							"&:hover": {
								bgcolor: "transparent",
								color: theme.palette.primary.main,
								textDecoration: "underline",
							},
						}}
					>
						Back to login
					</Button>
				</Box>
			</Box>
		</Box>
	);
};

export default OTPStep;
